import { useQuery } from 'urql';
import {
  GetTenantCreditsDocument,
  GetCreditsSummaryDocument,
  GetCreditPacksDocument,
} from 'src/gql/graphql';

export type CreditsInfo = {
  total: number;
  used: number;
  available: number;
  isProjectScoped: boolean;
  unlimited: boolean;
  loading: boolean;
  error: Error | undefined;
  renewalDate?: string;
  renewalAmount?: number; // totalCredits - the monthly credit allocation that will be renewed
};

/**
 * Hook to get credits information for display in the UI.
 * If a projectId is provided and the project has its own credit allocation,
 * returns project-scoped credits. Otherwise returns tenant credits.
 */
// eslint-disable-next-line complexity
export function useCredits(projectId?: string): CreditsInfo {
  // Always fetch tenant credits
  const [{ data: tenantData, fetching: tenantFetching, error: tenantError }] =
    useQuery({
      query: GetTenantCreditsDocument,
    });

  // Fetch project credits summary if we have a projectId
  const [
    { data: projectData, fetching: projectFetching, error: projectError },
  ] = useQuery({
    query: GetCreditsSummaryDocument,
    variables: { projectId: projectId ?? '' },
    pause: !projectId,
  });

  const loading = tenantFetching || projectFetching;
  const error = tenantError || projectError;

  // Check if project has its own allocation
  const hasProjectAllocation =
    projectData?.myCreditsSummary?.hasProjectAllocation ?? false;

  // Check if tenant has unlimited credits
  const unlimited =
    projectData?.myCreditsSummary?.unlimited ??
    tenantData?.myTenantCredits?.unlimited ??
    false;

  if (hasProjectAllocation && projectData?.myCreditsSummary) {
    const summary = projectData.myCreditsSummary;
    const tenantCredits = tenantData?.myTenantCredits;
    return {
      total: summary.projectAllocated,
      used: summary.projectUsed,
      available: summary.projectAvailable,
      isProjectScoped: true,
      unlimited,
      loading,
      error,
      // For project-scoped credits, show tenant renewal info
      renewalDate: tenantCredits?.renewalDate,
      renewalAmount: tenantCredits?.totalCredits, // Monthly allocation, not cap
    };
  }

  // Fall back to tenant credits
  const tenantCredits = tenantData?.myTenantCredits;
  return {
    total: tenantCredits?.totalCredits ?? 0,
    used: tenantCredits?.usedCredits ?? 0,
    available: tenantCredits?.availableCredits ?? 0,
    isProjectScoped: false,
    unlimited,
    loading,
    error,
    renewalDate: tenantCredits?.renewalDate,
    renewalAmount: tenantCredits?.totalCredits, // Monthly allocation, not cap
  };
}

/**
 * Hook to get tenant credits only
 */
export function useTenantCredits() {
  const [{ data, fetching, error }] = useQuery({
    query: GetTenantCreditsDocument,
  });

  return {
    credits: data?.myTenantCredits,
    loading: fetching,
    error,
  };
}

export type CreditPackInfo = {
  id: string;
  credits: number;
  usedCredits: number;
  availableCredits: number;
  purchasedAt: string;
  expiresAt: string;
};

/**
 * Hook to get credit packs for the current tenant
 * Returns credit packs ordered by purchase date (oldest first)
 */
export function useCreditPacks(
  requestPolicy?: 'cache-first' | 'network-only' | 'cache-and-network'
) {
  const [{ data, fetching, error }] = useQuery({
    query: GetCreditPacksDocument,
    requestPolicy: requestPolicy ?? 'cache-first',
  });

  return {
    creditPacks: (data?.myCreditPacks ?? []) as CreditPackInfo[],
    loading: fetching,
    error,
  };
}
